/* eslint-disable eqeqeq */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import VisitDetailsCard from "../../../components/card/VisitDetailsCard";
import { Button } from "../../../components/button";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import VisitorsGroupCard from "../../../components/card/VisitorsGroupCard";
import {
  AddAttachments,
  API_ERRORS,
  DOC_TYPE_ID,
  STATUS_IDS,
  USER_ROLES_IDS,
  VISIT_CREATED_FROM,
  Visitor,
  VisitorType,
} from "../../../constants/common";
import toast, { Toaster } from "react-hot-toast";
import {
  formattedDate,
  formattedDateTime,
  isVisitWithinVisitingHours,
  mapToOptions,
} from "../../../lib/functions";
import useAuthStore from "../../../store/Auth";
import { APP_ROUTES } from "../../../routes";
import {
  useAddAttachment,
  useAddVisitorAppointment,
  useCheckDuplicateAppointment,
} from "../../../hooks/visitor/mutation";
import { useLocation, useNavigate } from "react-router-dom";
import { createGroupSchema } from "../../../schemas/groupSchema";
import { v4 as uuidv4 } from "uuid";
import BackButton from "../../../components/button/BackButton";
import {
  useGetAllVisitPurposesDropdown,
  useGetVisitingHours,
} from "../../../hooks/common/query";
import VehicleInformationCard from "../../../components/card/VehicleInformationCard";
import CheckboxInput from "../../../components/inputs/CheckboxInput";

const Group = () => {
  const [isHostAccompanyPledge, setIsHostAccompanyPledge] = useState(false);

  const user = useAuthStore((state) => state.user);

  const { data: allVisitPurposes } = useGetAllVisitPurposesDropdown({
    encryptedVisitRequestTypeID: "",
    encryptedId: "",
  });

  const visitPurposes = useMemo(
    () =>
      mapToOptions(
        "s_PurposeEn",
        "s_PurposeAr",
        "n_PurposeID",
        allVisitPurposes
      ),
    [allVisitPurposes]
  );

  const [isVehicleInfoVisible, setIsVehicleInfoVisible] = useState(false);

  // const { data: dynamicWorkflow } = useGetDynamicWorkflowsByIdByNonAdmin({
  //   encryptedId: DYNAMIC_WORKFLOW.DEVICES_APPROVAL_ONE_DAY_VISIT,
  // });

  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    watch,
    setValue,
  } = useFormWithSchema(createGroupSchema(visitPurposes, user?.n_RoleID), {
    reValidateMode: "onChange",
    defaultValues: {
      hostEmail: user?.email,
      visitStartDate:
        new Date() > new Date(new Date().setHours(7, 0, 0, 0))
          ? formattedDateTime(new Date())
          : formattedDateTime(new Date(new Date().setHours(7, 0, 0, 0))),
      visitEndDate: formattedDateTime(
        new Date(
          (new Date() > new Date(new Date().setHours(7, 0, 0, 0))
            ? new Date()
            : new Date(new Date().setHours(7, 0, 0, 0))
          ).getTime() +
            60 * 60 * 1000
        )
      ),
    },
    context: {
      isVehicleInfoVisible,
      selectedIdType: DOC_TYPE_ID.NATIONAL_ID,
    },
  });

  const { t } = useTranslation();
  const visitReqType = watch("visitReqType");

  const location = useLocation();

  const uploadPhotoRef = useRef<HTMLInputElement | null>(null);
  const uploadEidRef = useRef<HTMLInputElement | null>(null);
  const uploadPassportRef = useRef<HTMLInputElement | null>(null);
  const uploadVehicleRegLicenseRef = useRef<HTMLInputElement | null>(null);
  const uploadHostEmployeeCardRef = useRef<HTMLInputElement | null>(null);
  const uploadIqamaRef = useRef<HTMLInputElement | null>(null);
  const uploadIdOtherRef = useRef<HTMLInputElement | null>(null);
  const uploadVisaRef = useRef<HTMLInputElement | null>(null);

  const [photoFileName, setPhotoFileName] = useState("");
  const [photoFileType, setPhotoFileType] = useState("");
  const [eidFileName, setEidFileName] = useState("");
  const [eidFileType, setEidFileType] = useState("");
  const [passportFileName, setPassportFileName] = useState("");
  const [passportFileType, setPassportFileType] = useState("");
  const [vehicleRegLicenseFileName, setVehicleRegLicenseFileName] =
    useState("");
  const [vehicleRegLicenseFileType, setVehicleRegLicenseFileType] =
    useState("");
  const [visaFileName, setVisaFileName] = useState("");
  const [visaFileType, setVisaFileType] = useState("");

  const [iqamaFileName, setIqamaFileName] = useState("");
  const [iqamaFileType, setIqamaFileType] = useState("");

  const [idOtherFileName, setIdOtherFileName] = useState("");
  const [idOtherFileType, setIdOtherFileType] = useState("");

  const [hostEmployeeCardFileName, setHostEmployeeCardFileName] = useState("");
  const [hostEmployeeCardFileType, setHostEmployeeCardFileType] = useState("");

  const [addedVisitors, setAddedVisitors] = useState<Visitor[]>([]);

  const {
    mutateAsync: AddVisitorAppointment,
    isPending: isAddVisitorAppointmentLoading,
  } = useAddVisitorAppointment();
  const { mutateAsync: AddAttachment, isPending: isAddAttachmentLoading } =
    useAddAttachment();
  const {
    mutateAsync: CheckDuplicateAppointment,
    isPending: isCheckDuplicateAppointmentLoading,
  } = useCheckDuplicateAppointment();

  const navigate = useNavigate();

  const selectedVisitPurpose = watch("visitPurpose");
  const visitStartDate = new Date(watch("visitStartDate") || "");
  const visitEndDate = new Date(watch("visitEndDate") || "");

  // const { mutateAsync: GetDeviceDetailsById } = useGetDeviceDetailsById();

  // const isMultiDayVisit =
  //   visitStartDate instanceof Date &&
  //   visitEndDate instanceof Date &&
  //   !isNaN(visitStartDate.getTime()) &&
  //   !isNaN(visitEndDate.getTime()) &&
  //   Math.floor(
  //     (visitEndDate.getTime() - visitStartDate.getTime()) /
  //       (1000 * 60 * 60 * 24)
  //   ) >= 1;

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      toast.error(t("fillAllInfo"));
    }
  }, [errors, t]);

  const isVisitPurposeOther = useMemo(() => {
    const purpose =
      visitPurposes?.find((item) => item.value === Number(selectedVisitPurpose))
        ?.labelEn || "";

    return purpose.trim() === "Other";
  }, [visitPurposes, selectedVisitPurpose]);

  const { data: visitingHours } = useGetVisitingHours();

  const visitAllowedDuration = visitingHours?.visitorsMaxAllowedDays - 1;

  // const { mutateAsync: ApproveDevices, isPending: isApproveDevicesLoading } =
  //   useApproveDevice();

  // const handleDevicesApproval = useCallback(
  //   async (
  //     appointmentId: string,
  //     devices: Device[],
  //     visitStartDate: string,
  //     visitEndDate: string
  //   ) => {
  //     if (appointmentId) {
  //       try {
  //         const body = {
  //           EncryptedId: appointmentId.toString(),
  //           n_RoleID: user?.n_RoleID,
  //           approvalDevices: devices?.map((item: Device) => ({
  //             appointmentDeviceId: item.appointmentDeviceId,
  //             N_ApprovalStatus: item.approvalStatus || 0,
  //             N_CurrentApprovalStatus: item.currentUserDeviceStatus || 0,
  //           })),
  //           Dt_AppointmentStartTime: visitStartDate,
  //           Dt_AppointmentEndTime: visitEndDate,
  //         };

  //         await ApproveDevices(body);
  //       } catch (error) {
  //         toast.error(t("somethingWentWrong"));
  //         return;
  //       }
  //     }
  //   },
  //   [user?.n_RoleID, ApproveDevices, t]
  // );

  const onSubmit = useCallback(
    async (data: any) => {
      if (addedVisitors.length === 0) {
        toast.error(t("kindlyAddVisitor"));
        return;
      }

      const isAppointmentWithinVisitingHours = isVisitWithinVisitingHours(
        data.visitStartDate,
        data.visitEndDate,
        visitingHours
      );

      if (new Date(data.visitEndDate) <= new Date(data.visitStartDate)) {
        toast.error(t("visitDateError"));
      } else if (!isAppointmentWithinVisitingHours) {
        toast.error(
          t("vistingHoursAreFrom") +
            visitingHours.visitorsStartTime +
            t("to") +
            visitingHours.visitorsEndTime +
            "."
        );
      } else {
        const commonData = {
          N_VisitCreatedFrom: VISIT_CREATED_FROM.VMS,
          N_VisitUpdatedFrom: VISIT_CREATED_FROM.VMS,
          N_RoleID: user?.n_RoleID,
          N_VisitType: Number(data.visitReqType),
          N_LocationID: Number(data.location),
          N_BuildingID: Number(data.building),
          N_DepartmentID: Number(data.visitingDepartment),
          n_GateID: Number(data.gate),
          n_MeetingRoomID: Number(data.meetingRoom),
          S_VisitingPersonEmail: data.hostEmail,
          S_HostMobileNo: data.hostMobileNumber,

          Dt_AppointmentStartTime: formattedDateTime(
            new Date(data.visitStartDate)
          ),
          Dt_AppointmentEndTime: formattedDateTime(new Date(data.visitEndDate)),

          N_PlateSource: isVehicleInfoVisible ? Number(data.plateSource) : 0,
          S_PlateCode: isVehicleInfoVisible ? data.plateCode : "",
          S_PlateNumber: isVehicleInfoVisible ? data.plateNumber : 0,
          N_PlateLetter1: isVehicleInfoVisible ? Number(data.plateLetter1) : 0,
          N_PlateLetter2: isVehicleInfoVisible ? Number(data.plateLetter2) : 0,
          N_PlateLetter3: isVehicleInfoVisible ? Number(data.plateLetter3) : 0,

          ApprovalStatus: STATUS_IDS.PENDING,
          Purpose: Number(data.visitPurpose),
          PurposeOtherValue: isVisitPurposeOther ? data.visitPurposeOther : "",
          Remarks: data.note,
          S_GroupKey: uuidv4(),
          user_id: user?.user_id,
          N_IsExternalAppointment: false,
          N_CreatedBy_Staff: user?.user_id,
          N_UpdatedBy_Staff: user?.user_id,
          N_CreatedBy_External: 0,
          N_IsMobileAllowed: false,
          N_IsLaptopAllowed: false,
          S_AllowedReason: "",
          n_SelfPass: 2,
          s_HostAccessAreasIDs: data.reqAccessLevels,
          S_MaxAccessLevelIDs: data.accessLevels || null,
        };

        const apiCalls: Promise<void>[] = [];
        let duplicateAppointmentIds;

        for (let i = 0; i < addedVisitors.length; i++) {
          var item = addedVisitors[i];

          try {
            if (user?.user_id) {
              const body = {
                N_AppointmentID: 0,
                N_ExternalRegistrationID: 0,
                N_LocationID: Number(data.location),
                N_BuildingID: Number(data.building),
                S_Iqama:
                  item.S_Iqama && item.IDType === DOC_TYPE_ID.IQAMA
                    ? item.S_Iqama
                    : "",
                S_EIDNumber:
                  item.EIDNumber && item.IDType === DOC_TYPE_ID.NATIONAL_ID
                    ? item.EIDNumber
                    : "",
                S_PassportNumber:
                  item.PassportNumber && item.IDType === DOC_TYPE_ID.PASSPORT
                    ? item.PassportNumber
                    : "",
                S_OthersValue:
                  item.S_OthersValue && item.IDType === DOC_TYPE_ID.OTHER
                    ? item.S_OthersValue
                    : "",
                S_VisaNo:
                  item.S_VisaNo && item.IDType === DOC_TYPE_ID.VISA
                    ? item.S_VisaNo
                    : "",

                D_FromDate: formattedDateTime(new Date(data.visitStartDate)),
                D_ToDate: formattedDateTime(new Date(data.visitEndDate)),
              };
              const res = await CheckDuplicateAppointment(body);

              addedVisitors[i].isDuplicate = res.status;
            }
          } catch (error) {
            toast.error(t("somethingWentWrong"));
            return;
          }

          const duplicateAppointments = addedVisitors.filter(
            (item) => item.isDuplicate
          );

          duplicateAppointmentIds = duplicateAppointments.map((item) => {
            const idNumber =
              item.S_Iqama ||
              item.EIDNumber ||
              item.PassportNumber ||
              item.S_VisaNo ||
              item.S_OthersValue;

            return idNumber;
          });

          if (duplicateAppointments.length === 0) {
            try {
              const body = {
                ...commonData,
                ...item,

                Dt_DateOfBirth: item.dateOfBirth
                  ? formattedDate(new Date(item.dateOfBirth), true)
                  : null,

                Dt_IqamaExpiry:
                  item.Dt_IqamaExpiry && item.IDType === DOC_TYPE_ID.IQAMA
                    ? formattedDate(new Date(item.Dt_IqamaExpiry), true)
                    : "",
                Dt_EIDExpiryDate:
                  item.Dt_EIDExpiryDate &&
                  item.IDType === DOC_TYPE_ID.NATIONAL_ID
                    ? formattedDate(new Date(item.Dt_EIDExpiryDate), true)
                    : "",
                Dt_PassportExpiryDate:
                  item.Dt_PassportExpiryDate &&
                  item.IDType === DOC_TYPE_ID.PASSPORT
                    ? formattedDate(new Date(item.Dt_PassportExpiryDate), true)
                    : "",
                Dt_OthersExpiry:
                  item.Dt_OthersExpiry && item.IDType === DOC_TYPE_ID.OTHER
                    ? formattedDate(new Date(item.Dt_OthersExpiry), true)
                    : "",
                Dt_VisaExpiryDate:
                  item.Dt_VisaExpiryDate && item.IDType === DOC_TYPE_ID.VISA
                    ? formattedDate(new Date(item.Dt_VisaExpiryDate), true)
                    : "",

                uploadPhoto: "",
                uploadEid: "",
                uploadPassport: "",
                uploadIqama: "",
                uploadIdOther: "",
                uploadVisa: "",

                photoFileType: "",
                eidFileType: "",
                passportFileType: "",
                iqamaFileType: "",
                idOtherFileType: "",
                visaFileType: "",
              };

              const res = await AddVisitorAppointment(body);
              if (res) {
                const appointmentID = res.id;

                // if (
                //   isDeviceDetails &&
                //   dynamicWorkflow?.[0].n_RoleID === user?.n_RoleID
                // ) {
                //   const body = {
                //     encryptedId: appointmentID,
                //   };
                //   const devices = await GetDeviceDetailsById(body);

                //   handleDevicesApproval(
                //     appointmentID,
                //     devices,
                //     formattedDateTime(new Date(data.visitStartDate)),
                //     formattedDateTime(new Date(data.visitEndDate))
                //   );
                // }

                const attachmentBodies: AddAttachments[] = [
                  {
                    Id: appointmentID,
                    File: item.uploadPhoto,
                    S_FileType: item.photoFileType,
                    S_FileFieldName: "S_PhotoUpload",
                    S_FiledFileType: "S_PhotoContentType",
                  },
                  {
                    Id: appointmentID,
                    File:
                      item.IDType === DOC_TYPE_ID.IQAMA ? item.uploadIqama : "",
                    S_FileType:
                      item.IDType === DOC_TYPE_ID.IQAMA
                        ? item.iqamaFileType
                        : "",
                    S_FileFieldName: "S_IqamaUpload",
                    S_FiledFileType: "S_IqamaContentType",
                  },
                  {
                    Id: appointmentID,
                    File:
                      item.IDType === DOC_TYPE_ID.NATIONAL_ID
                        ? item.uploadEid
                        : "",
                    S_FileType:
                      item.IDType === DOC_TYPE_ID.NATIONAL_ID
                        ? item.eidFileType
                        : "",
                    S_FileFieldName: "S_EIDFile",
                    S_FiledFileType: "S_EIDContentType",
                  },
                  {
                    Id: appointmentID,
                    File:
                      item.IDType === DOC_TYPE_ID.PASSPORT
                        ? item.uploadPassport
                        : "",
                    S_FileType:
                      item.IDType === DOC_TYPE_ID.PASSPORT
                        ? item.passportFileType
                        : "",
                    S_FileFieldName: "S_PassportFile",
                    S_FiledFileType: "S_PassportContentType",
                  },
                  {
                    Id: appointmentID,
                    File:
                      item.IDType === DOC_TYPE_ID.OTHER
                        ? item.uploadIdOther
                        : "",
                    S_FileType:
                      item.IDType === DOC_TYPE_ID.OTHER
                        ? item.idOtherFileType
                        : "",
                    S_FileFieldName: "S_OthersUpload",
                    S_FiledFileType: "S_OthersContentType",
                  },
                  {
                    Id: appointmentID,
                    File:
                      item.IDType === DOC_TYPE_ID.VISA ? item.uploadVisa : "",
                    S_FileType:
                      item.IDType === DOC_TYPE_ID.VISA ? item.visaFileType : "",
                    S_FileFieldName: "S_VisaFile",
                    S_FiledFileType: "S_VisaContentType",
                  },
                  {
                    Id: appointmentID,
                    File: item.uploadVehicleRegLicense,
                    S_FileType: item.vehicleRegLicenseFileType,
                    S_FileFieldName: "S_VehicleRegistrationFile",
                    S_FiledFileType: "S_VehicleRegistrationContentType",
                  },
                  {
                    Id: appointmentID,
                    File: item.uploadHostEmployeeCard,
                    S_FileType: item.hostEmployeeCardFileType,
                    S_FileFieldName: "S_HostEmployeeCardFile",
                    S_FiledFileType: "S_HostEmployeeCardContentType",
                  },
                ];

                const formDatas = attachmentBodies.map((body) => {
                  const formData = new FormData();
                  formData.append("Id", body.Id.toString());
                  formData.append("File", body.File ?? "");
                  formData.append("S_FileType", body.S_FileType ?? "");
                  formData.append("S_FileFieldName", body.S_FileFieldName);
                  formData.append("S_FiledFileType", body.S_FiledFileType);
                  return formData;
                });

                const uploadPromises: Promise<void>[] = formDatas.map(
                  (formData) => {
                    if (formData.get("File")) {
                      return AddAttachment(formData);
                    }
                    return Promise.resolve();
                  }
                );

                apiCalls.push(...uploadPromises);
              }

              await Promise.all(apiCalls);

              toast.success(t("appointmentCreated"));

              setTimeout(() => {
                navigate(APP_ROUTES.SEARCH_VISIT_REQUEST);
              }, 700);
            } catch (error: any) {
              const errorCode = error.errors && Object.keys(error.errors)[0];

              if (errorCode === API_ERRORS.MAX_ALLOWED_DAYS_EXCEEDED) {
                toast.error(t("maxAllowedDaysExceeded"));
              } else {
                toast.error(t("somethingWentWrong"));
              }
            }
          }
        }

        if (duplicateAppointmentIds && duplicateAppointmentIds.length > 0) {
          toast.error(
            t("appointmentExistsForIdNumber") + duplicateAppointmentIds
          );
        }
      }
    },
    [
      addedVisitors,
      visitingHours,
      t,
      user?.n_RoleID,
      user?.user_id,
      isVehicleInfoVisible,
      isVisitPurposeOther,
      CheckDuplicateAppointment,
      AddVisitorAppointment,
      AddAttachment,
      navigate,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-1 justify-between">
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("createVisitRequest")}{" "}
            <span className="text-sm">({t("group")})</span>
          </h1>

          <div className="flex items-center gap-2">
            {location.pathname === APP_ROUTES.APPROVE_REQUEST ? (
              <Button
                type="button"
                onClick={() => navigate(APP_ROUTES.APPROVE_VISIT_REQUEST)}
                className="max-w-max bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent dark:!ring-white dark:text-white"
              >
                {t("approveVisitRequests")}
              </Button>
            ) : user?.n_RoleID !== USER_ROLES_IDS.SECURITY ? (
              <Button
                type="button"
                onClick={() => navigate(APP_ROUTES.SEARCH_VISIT_REQUEST)}
                className="max-w-max bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent dark:!ring-white dark:text-white"
              >
                {t("searchVisitRequests")}
              </Button>
            ) : (
              <></>
            )}
            <BackButton />
          </div>
        </div>

        <VisitDetailsCard
          control={control}
          errors={errors}
          clearErrors={clearErrors}
          cardNumber={1}
          isVIP={visitReqType == VisitorType.VVIP}
          visitStartDate={visitStartDate}
          setValue={setValue}
          visitPurposes={visitPurposes}
          isVisitPurposeOther={isVisitPurposeOther}
          isVehicleInfoVisible={isVehicleInfoVisible}
          setIsVehicleInfoVisible={setIsVehicleInfoVisible}
          visitAllowedDuration={visitAllowedDuration}
        />

        {isVehicleInfoVisible && (
          <VehicleInformationCard
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            isVehicleInfoVisible={isVehicleInfoVisible}
          />
        )}

        <VisitorsGroupCard
          uploadPhotoRef={uploadPhotoRef}
          photoFileName={photoFileName}
          setPhotoFileName={setPhotoFileName}
          uploadEidRef={uploadEidRef}
          eidFileName={eidFileName}
          setEidFileName={setEidFileName}
          addedVisitors={addedVisitors}
          setAddedVisitors={setAddedVisitors}
          setPhotoFileType={setPhotoFileType}
          eidFileType={eidFileType}
          setEidFileType={setEidFileType}
          uploadPassportRef={uploadPassportRef}
          passportFileName={passportFileName}
          setPassportFileName={setPassportFileName}
          setPassportFileType={setPassportFileType}
          passportFileType={passportFileType}
          uploadVisaRef={uploadVisaRef}
          visaFileName={visaFileName}
          setVisaFileName={setVisaFileName}
          setVisaFileType={setVisaFileType}
          visaFileType={visaFileType}
          photoFileType={photoFileType}
          isVIP={visitReqType == VisitorType.VVIP}
          uploadVehicleRegLicenseRef={uploadVehicleRegLicenseRef}
          setVehicleRegLicenseFileName={setVehicleRegLicenseFileName}
          setVehicleRegLicenseFileType={setVehicleRegLicenseFileType}
          vehicleRegLicenseFileName={vehicleRegLicenseFileName}
          vehicleRegLicenseFileType={vehicleRegLicenseFileType}
          uploadIqamaRef={uploadIqamaRef}
          iqamaFileName={iqamaFileName}
          setIqamaFileName={setIqamaFileName}
          setIqamaFileType={setIqamaFileType}
          iqamaFileType={iqamaFileType}
          uploadIdOtherRef={uploadIdOtherRef}
          idOtherFileName={idOtherFileName}
          setIdOtherFileName={setIdOtherFileName}
          setIdOtherFileType={setIdOtherFileType}
          idOtherFileType={idOtherFileType}
          uploadHostEmployeeCardRef={uploadHostEmployeeCardRef}
          hostEmployeeCardFileName={hostEmployeeCardFileName}
          hostEmployeeCardFileType={hostEmployeeCardFileType}
          setHostEmployeeCardFileName={setHostEmployeeCardFileName}
          setHostEmployeeCardFileType={setHostEmployeeCardFileType}
          visitEndDate={visitEndDate}
          cardNumber={isVehicleInfoVisible ? 3 : 2}
          clearErrors={clearErrors}
        />

        {/* {user?.n_RoleID === USER_ROLES_IDS.SECURITY && (
          <AccessLevelsCard
            control={control}
            errors={errors}
            cardNumber={isDeviceDetails ? 5 : 4}
          />
        )} */}

        <div className="flex flex-col gap-3 bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
          <CheckboxInput
            id="host-accompany-pledge"
            label={t("hostAccompanyPledge")}
            labelStyles="leading-[1.2]"
            checked={isHostAccompanyPledge}
            setChecked={setIsHostAccompanyPledge}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button
            className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
            onClick={handleSubmit(onSubmit)}
            disabled={
              !isHostAccompanyPledge ||
              isAddVisitorAppointmentLoading ||
              isAddAttachmentLoading ||
              isCheckDuplicateAppointmentLoading
              // isApproveDevicesLoading
            }
            isLoading={
              isAddVisitorAppointmentLoading ||
              isAddAttachmentLoading ||
              isCheckDuplicateAppointmentLoading
              // isApproveDevicesLoading
            }
          >
            {t("submit")}
          </Button>
        </div>
      </div>
    </>
  );
};

export default Group;
